import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import { Checkbox, FormControlLabel, Button, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import axios from "axios";
import React, { useEffect, useState, useMemo } from "react";
import InputSelect from "../../common/InputSelect";
import Loader from "../../common/loader/Loader";
import { toast } from "react-toastify";
import "./AgentsLinetypesHide.css";

const DAYS = [
  { key: "Mon", label: "Monday" },
  { key: "Tue", label: "Tuesday" },
  { key: "Wed", label: "Wednesday" },
  { key: "Thu", label: "Thursday" },
  { key: "Fri", label: "Friday" },
  { key: "Sat", label: "Saturday" },
  { key: "Sun", label: "Sunday" },
];

const emptyWeekly = () =>
  DAYS.reduce((acc, d) => ({ ...acc, [d.key]: { open: "", close: "" } }), {});

const toTimeInput = (val) => (val ? String(val).slice(0, 5) : "");

function AgentLinetypeHide() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("userInfo")),
    []
  );

  const [idConfiguration, setIdConfiguration] = React.useState(null);
  const [lineTypes, setLineTypes] = React.useState([]);
  const [lineTypeId, setLineTypeId] = React.useState();
  const [sports, setSports] = React.useState([]);
  const [sportId, setSportId] = React.useState();
  const [leagues, setLeagues] = React.useState([]);
  const [leagueId, setLeagueId] = React.useState();
  const [openHour, setOpenHour] = React.useState("");
  const [closeHour, setCloseHour] = React.useState("");
  const [isWeekly, setIsWeekly] = React.useState(false);
  const [weeklySchedule, setWeeklySchedule] = React.useState(emptyWeekly());

  const useStyles = makeStyles({
    root: {
      width: "100%",
      overflowX: "auto",
    },
    table: {
      minWidth: 650,
    },
    tableHead: {
      background: "#3f51b5",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "darkcyan !important",
      },
    },
    tableRow: {
      "&:hover": {
        backgroundColor: "lightgrey !important",
      },
    },
    headerCell: {
      color: "white",
    },
  });

  const classes = useStyles();

  useEffect(() => {
    GetLineTypes();
    GetSports();
  }, []);

  useEffect(() => {
    if (sportId) {
      GetLeagues();
    }
  }, [sportId]);

  useEffect(() => {
    if (lineTypeId && sportId && leagueId) {
      LoadExistingConfiguration();
    }
  }, [lineTypeId, sportId, leagueId]);

  const GetLineTypes = () => {
    const url = process.env.REACT_APP_API2_URL + "/LineType/GetLineTypes";
    axios
      .get(url)
      .then((response) => response.data)
      .then((data) => {
        setLineTypes(data.map((v) => ({ ...v })));
      })
      .finally(() => setIsLoading(false));
  };

  const GetSports = () => {
    const url = process.env.REACT_APP_API2_URL + "/League/GetAllSports";
    axios
      .get(url)
      .then((response) => response.data)
      .then((data) => setSports(data));
  };

  const GetLeagues = () => {
    if (!sportId) return;
    const url =
      process.env.REACT_APP_API2_URL +
      "/League/League_GetLeaguesByIdSport?idSport=" +
      sportId;
    axios
      .get(url)
      .then((response) => response.data)
      .then((data) => {
        setLeagues(data);
        setLeagueId(null);
      });
  };

  const LoadExistingConfiguration = () => {
    const url =
      process.env.REACT_APP_API2_URL +
      `/LineType/LineTypeHideConfigurationGetById?idLineType=${lineTypeId}&idSport=${sportId}&idLeague=${leagueId}`;

    axios
      .get(url)
      .then((response) => response.data)
      .then((data) => {
        if (data && data.idConfiguration) {
          setIdConfiguration(data.idConfiguration);
          setIsWeekly(data.isWeekly);

          if (data.isWeekly) {
            setOpenHour("");
            setCloseHour("");
            setWeeklySchedule(
              DAYS.reduce(
                (acc, d) => ({
                  ...acc,
                  [d.key]: {
                    open:  toTimeInput(data[`${d.key.toLowerCase()}_OpenHour`]),
                    close: toTimeInput(data[`${d.key.toLowerCase()}_CloseHour`]),
                  },
                }),
                {}
              )
            );
          } else {
            setOpenHour(toTimeInput(data.openHour));
            setCloseHour(toTimeInput(data.closeHour));
            setWeeklySchedule(emptyWeekly());
          }

          toast.info("Existing configuration loaded");
        } else {
          resetForm();
        }
      })
      .catch(() => resetForm());
  };

  const resetForm = () => {
    setIdConfiguration(null);
    setIsWeekly(false);
    setOpenHour("");
    setCloseHour("");
    setWeeklySchedule(emptyWeekly());
  };

  const handleWeeklyDayChange = (dayKey, field, value) => {
    setWeeklySchedule((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], [field]: value },
    }));
  };

  const isFormValid = () => {
    if (!lineTypeId || !sportId || !leagueId) return false;
    if (!isWeekly) return !!(openHour && closeHour);
    return DAYS.some((d) => weeklySchedule[d.key].open || weeklySchedule[d.key].close);
  };

  const handleSave = () => {
    if (!lineTypeId) { toast.error("Please select a Line Type"); return; }
    if (!sportId)    { toast.error("Please select a Sport");     return; }
    if (!leagueId)   { toast.error("Please select a League");    return; }

    if (!isWeekly) {
      if (!openHour || !closeHour) {
        toast.error("Please enter both Open Hour and Close Hour");
        return;
      }
    } else {
      const hasAny = DAYS.some((d) => weeklySchedule[d.key].open || weeklySchedule[d.key].close);
      if (!hasAny) {
        toast.error("Please enter at least one day's schedule");
        return;
      }
    }

    setIsSaving(true);

    const requestDto = {
      idConfiguration: idConfiguration,
      idLineType:      lineTypeId,
      idSport:         sportId || null,
      idLeague:        leagueId || null,
      isWeekly:        isWeekly,
      openHour:        !isWeekly ? openHour  : null,
      closeHour:       !isWeekly ? closeHour : null,
      ...DAYS.reduce(
        (acc, d) => ({
          ...acc,
          [`${d.key}_OpenHour`]:  isWeekly ? (weeklySchedule[d.key].open  || null) : null,
          [`${d.key}_CloseHour`]: isWeekly ? (weeklySchedule[d.key].close || null) : null,
        }),
        {}
      ),
      idUser: userInfo.UserId,
    };

    axios
      .post(
        process.env.REACT_APP_API2_URL + "/LineType/LineTypeHideConfigurationUpsert",
        requestDto
      )
      .then((response) => {
        if (response.data && response.data.idConfiguration) {
          setIdConfiguration(response.data.idConfiguration);
          toast.success(
            response.data.Action === "Inserted"
              ? "Configuration created successfully!"
              : "Configuration updated successfully!"
          );
        } else {
          toast.success("Configuration saved successfully!");
        }
      })
      .catch(() => toast.error("Error saving configuration. Please try again."))
      .finally(() => setIsSaving(false));
  };

  return (
    <div id="main-div">
      <Paper className={`${classes.root} flex flex-col gap-4`}>
        <h2>Hide games by linetypes</h2>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="form-row">
              <Autocomplete
                options={lineTypes}
                getOptionLabel={(option) => option.description || ""}
                getOptionSelected={(option, value) => option.idLineType === value.idLineType}
                value={lineTypes.find((lt) => lt.idLineType === lineTypeId) || null}
                onChange={(_, newValue) =>
                  setLineTypeId(newValue ? newValue.idLineType : null)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Line Type"
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                style={{ minWidth: 200 }}
              />

              <InputSelect
                variant="outlined"
                size="small"
                label="Sport"
                name="sport"
                data={sports}
                placeholder="Sports"
                setSelectValue={(val) => setSportId(val)}
                startValue={sportId}
              />

              <Autocomplete
                options={leagues}
                getOptionLabel={(option) => option.description || ""}
                getOptionSelected={(option, value) => option.idLeague === value.idLeague}
                value={leagues.find((l) => l.idLeague === leagueId) || null}
                onChange={(_, newValue) =>
                  setLeagueId(newValue ? newValue.idLeague : null)
                }
                disabled={!sportId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="League"
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                style={{ minWidth: 200 }}
              />
            </div>

            <div className="form-row">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isWeekly}
                    onChange={(e) => setIsWeekly(e.target.checked)}
                    color="primary"
                  />
                }
                label="Weekly"
              />
            </div>

            {!isWeekly ? (
              <div className="form-row">
                <div className="time-group">
                  <label htmlFor="openHour">Open at:</label>
                  <input
                    id="openHour"
                    type="time"
                    value={openHour}
                    onChange={(e) => setOpenHour(e.target.value)}
                  />
                </div>
                <div className="time-group">
                  <label htmlFor="closeHour">Close at:</label>
                  <input
                    id="closeHour"
                    type="time"
                    value={closeHour}
                    onChange={(e) => setCloseHour(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-3 border-b text-base">Day</th>
                    <th className="text-left px-4 py-3 border-b text-base">Open</th>
                    <th className="text-left px-4 py-3 border-b text-base">Close</th>
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map((d) => (
                    <tr key={d.key} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-base w-40">{d.label}</td>
                      <td className="px-4 py-3">
                        <input
                          type="time"
                          value={weeklySchedule[d.key].open}
                          onChange={(e) =>
                            handleWeeklyDayChange(d.key, "open", e.target.value)
                          }
                          style={{ fontSize: "1rem", padding: "6px 10px" }}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="time"
                          value={weeklySchedule[d.key].close}
                          onChange={(e) =>
                            handleWeeklyDayChange(d.key, "close", e.target.value)
                          }
                          style={{ fontSize: "1rem", padding: "6px 10px" }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="form-row mt-4">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isSaving || !isFormValid()}
              >
                {isSaving ? "Saving..." : "Save Configuration"}
              </Button>
            </div>
          </>
        )}
      </Paper>
    </div>
  );
}

export default AgentLinetypeHide;
